import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivityLogService } from '../../../../core/services/activity-log.service';

interface ActivityLog {
  _id: string;
  action: string;
  actorId: string;
  actorType: string;
  actorIp: string;
  actorName: string;
  passportNumber: string;
  metadata: {
    description: string;
    content: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ApiResponse {
  data: ActivityLog[];
  pagination: PaginationInfo;
}

@Component({
  selector: 'app-activities',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './activities.component.html',
  styleUrls: ['./activities.component.scss'],
})
export class ActivitiesComponent implements OnInit {
  activities: ActivityLog[] = [];
  filteredActivities: ActivityLog[] = [];
  searchTerm: string = '';
  selectedStatus: string = 'all';
  currentPage: number = 1;
  itemsPerPage: number = 10;
  loading: boolean = false;
  error: string | null = null;
  pagination: PaginationInfo | null = null;

  constructor(private activityLogService: ActivityLogService) {}

  ngOnInit() {
    this.loadActivities();
  }

  loadActivities(page: number = 1) {
    this.loading = true;
    this.error = null;
    this.currentPage = page;

    this.activityLogService
      .getMyActivityLogs(page, this.itemsPerPage)
      .subscribe({
        next: (response: ApiResponse) => {
          this.activities = response.data || [];
          this.pagination = response.pagination;
          this.filteredActivities = [...this.activities];
          this.loading = false;
        },
        error: (err: any) => {
          console.error('Erreur lors du chargement des activités:', err);
          this.error = 'Erreur lors du chargement des activités';
          this.loading = false;
          // Fallback vers les données mockées en cas d'erreur
          this.loadMockData();
        },
      });
  }

  loadMockData() {
    this.activities = [
      {
        _id: '1',
        action: 'LOGIN',
        actorId: 'user1',
        actorType: 'USER',
        actorIp: '192.168.1.100',
        actorName: 'Utilisateur Test',
        passportNumber: 'PM123456789',
        metadata: {
          description: "Connexion réussie à l'application",
          content: "L'utilisateur s'est connecté avec succès",
        },
        createdAt: '2024-01-15T10:30:00Z',
        updatedAt: '2024-01-15T10:30:00Z',
      },
      {
        _id: '2',
        action: 'PROFILE_UPDATE',
        actorId: 'user1',
        actorType: 'USER',
        actorIp: '192.168.1.100',
        actorName: 'Utilisateur Test',
        passportNumber: 'PM123456789',
        metadata: {
          description: 'Modification du profil utilisateur',
          content: 'Informations personnelles mises à jour',
        },
        createdAt: '2024-01-14T15:45:00Z',
        updatedAt: '2024-01-14T15:45:00Z',
      },
    ];
    this.filteredActivities = [...this.activities];
    this.pagination = {
      page: 1,
      limit: 10,
      total: this.activities.length,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    };
  }

  onSearchChange() {
    this.filterActivities();
  }

  onStatusChange() {
    this.filterActivities();
  }

  filterActivities() {
    this.filteredActivities = this.activities.filter((activity) => {
      const matchesSearch =
        activity.action.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        activity.metadata.description
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase()) ||
        activity.metadata.content
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase()) ||
        activity.actorName
          .toLowerCase()
          .includes(this.searchTerm.toLowerCase());

      // Filtrer par type d'action
      let matchesStatus = this.selectedStatus === 'all';
      if (this.selectedStatus === 'success') {
        matchesStatus = activity.action === 'LOGIN';
      } else if (this.selectedStatus === 'warning') {
        matchesStatus =
          activity.action === 'PROFILE_UPDATE' ||
          activity.action === 'PASSWORD_CHANGE';
      } else if (this.selectedStatus === 'error') {
        matchesStatus =
          activity.action !== 'LOGIN' &&
          activity.action !== 'PROFILE_UPDATE' &&
          activity.action !== 'PASSWORD_CHANGE';
      }

      return matchesSearch && matchesStatus;
    });
  }

  get paginatedActivities(): ActivityLog[] {
    return this.filteredActivities;
  }

  get totalPages(): number {
    return this.pagination?.totalPages || 1;
  }

  get pagesArray(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(
      1,
      this.currentPage - Math.floor(maxVisiblePages / 2)
    );
    const endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  changePage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.loadActivities(page);
    }
  }

  getStatusClass(action: string): string {
    // Déterminer le statut basé sur l'action
    switch (action) {
      case 'LOGIN':
        return 'bg-green-100 text-green-800';
      case 'LOGOUT':
        return 'bg-blue-100 text-blue-800';
      case 'PROFILE_UPDATE':
        return 'bg-yellow-100 text-yellow-800';
      case 'PASSWORD_CHANGE':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getStatusText(action: string): string {
    switch (action) {
      case 'LOGIN':
        return 'Connexion';
      case 'LOGOUT':
        return 'Déconnexion';
      case 'PROFILE_UPDATE':
        return 'Modification profil';
      case 'PASSWORD_CHANGE':
        return 'Changement mot de passe';
      case 'REQUEST_PASSWORD_RESET':
        return 'Réinitialisation de mot de passe';
      case 'USER_UPDATE':
        return 'Mise à jour infos';
      case 'USER_CREATE':
        return 'Création utilisateur';
      case 'USER_DELETE':
        return 'Suppression utilisateur';
      case 'USER_RESET_PASSWORD':
      default:
        return action;
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Getters pour les statistiques
  get totalActivities(): number {
    return this.pagination?.total || this.activities.length;
  }

  get successActivities(): number {
    return this.activities.filter((activity) => activity.action === 'LOGIN')
      .length;
  }

  get warningActivities(): number {
    return this.activities.filter(
      (activity) =>
        activity.action === 'PROFILE_UPDATE' ||
        activity.action === 'PASSWORD_CHANGE'
    ).length;
  }

  get errorActivities(): number {
    return this.activities.filter(
      (activity) =>
        activity.action !== 'LOGIN' &&
        activity.action !== 'PROFILE_UPDATE' &&
        activity.action !== 'PASSWORD_CHANGE'
    ).length;
  }

  get currentPageEndIndex(): number {
    return Math.min(
      this.currentPage * this.itemsPerPage,
      this.filteredActivities.length
    );
  }

  refreshActivities() {
    this.loadActivities(this.currentPage);
  }
}
